import {
  CButton,
  CCol,
  CFormGroup,
  CInput,
  CRow,
  CLabel,
  CBadge,
  CTooltip,
} from "@coreui/react";
import React, { useCallback, useState } from "react";
import { FieldErrors } from "../form/FieldErrors";
import Errors, { errorsAreSame, getFieldErrors } from "../../models/errors";
import { emptyValueOnUndefined } from "../../utils/fields";
import InventoryInputBatch from "../../models/inventory-input-batch";
import ProductionOrderInputMaterial from "../../models/production-input-material";
import { getItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { warningAlert } from "../utils/messages";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import InputBatchSelect from "../inventory-input-batches/InputBatchSelect";

interface ProductionInputMaterialFormProps {
  value: ProductionOrderInputMaterial;
  initialBatch?: InventoryInputBatch;
  initialAmount?: number;
  disabled?: boolean;
  committedFromOthers?: number;
  isDuplicateInOrder?: boolean;
  isOvercommitted?: boolean;
  onDelete: (_: ProductionOrderInputMaterial) => void;
  onChange: (_: ProductionOrderInputMaterial) => void;
  errors: Errors;
}

const ProductionInputMaterialForm: React.FC<
  ProductionInputMaterialFormProps
> = ({
  value,
  initialBatch,
  initialAmount,
  disabled,
  committedFromOthers = 0,
  isDuplicateInOrder,
  isOvercommitted,
  onDelete,
  onChange,
  errors,
}) => {
  const [editingItem, setEditingItem] =
    useState<ProductionOrderInputMaterial>(value);
  const [batch, setBatch] = useState<InventoryInputBatch | null>(
    initialBatch !== undefined ? initialBatch : null,
  );
  const [barcodeInput, setBarcodeInput] = useState("");
  const [loadingBarcode, setLoadingBarcode] = useState(false);

  const applyBatch = useCallback(
    (newBatch: InventoryInputBatch | null) => {
      setBatch(newBatch);
      // Use existing amount, fall back to 1 if not yet set
      const currentAmount = editingItem.amount ?? 1;
      const newItem: ProductionOrderInputMaterial = {
        ...editingItem,
        inventoryInputBatch: newBatch ?? undefined,
        inventoryInputBatchId: newBatch?.id,
        inputMaterial: newBatch?.inputMaterial,
        inputMaterialId: newBatch?.inputMaterialId,
        inputMaterialIdentifier: newBatch?.inputMaterialIdentifier,
        isFabric: newBatch?.inputMaterial?.type === "fabric",
        amount: currentAmount,
        // Always calculate price if unit price is available
        price:
          newBatch?.unitPrice != null
            ? newBatch.unitPrice * currentAmount
            : undefined,
      };
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  const onBarcodeKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || !barcodeInput.trim()) return;
    setLoadingBarcode(true);
    const result = await getItem<InventoryInputBatch>(
      `/inventory_locations/batches/by_barcode/${barcodeInput.trim()}/`,
    );
    setLoadingBarcode(false);
    setBarcodeInput("");
    if (result.status === SUCCESS && result.data) {
      applyBatch(result.data);
    } else {
      warningAlert("No se encontró ningún lote con ese código de barra.");
    }
  };

  const availableInBatch = batch
    ? (batch.amount ?? 0) - (batch.amountUsed ?? 0)
    : null;

  // What can actually be used: physical availability minus what other pending
  // (not yet discounted) orders already committed for this batch.
  const effectiveAvailable =
    availableInBatch != null ? availableInBatch - committedFromOthers : null;

  const onBatchSelectChange = useCallback(
    (newBatch: InventoryInputBatch | null) => {
      applyBatch(newBatch);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  const onAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = Number(e.target.value);

      if (newValue < 0) newValue = 0;
      if (availableInBatch != null && newValue > availableInBatch) {
        newValue = availableInBatch;
      }

      const unitPrice = editingItem.inventoryInputBatch?.unitPrice;
      const newItem = {
        ...editingItem,
        amount: newValue,
        price: unitPrice != null ? unitPrice * newValue : undefined,
      };
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem, availableInBatch],
  );

  const onFixedAmountChange = useCallback(
    (isFixed: boolean) => {
      const newItem = { ...editingItem, isFixedAmount: isFixed };
      setEditingItem(newItem);
      onChange(newItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingItem],
  );

  const onDeleteClick = useCallback(() => {
    onDelete(editingItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem]);

  const highlightClassName = isOvercommitted
    ? "border border-danger"
    : isDuplicateInOrder
      ? "border border-warning"
      : "border";

  return (
    <div className={`section ${highlightClassName} rounded mb-2 p-2`}>
      {(isDuplicateInOrder || isOvercommitted) && (
        <CRow>
          <CCol className="mb-2">
            {isDuplicateInOrder && (
              <small className="text-warning d-block">
                <i className="fa fa-exclamation-triangle" /> Este lote está
                repetido en esta orden.
              </small>
            )}
            {isOvercommitted && (
              <small className="text-danger d-block">
                <i className="fa fa-exclamation-triangle" /> La cantidad total
                de este lote (esta orden y otras pendientes de descontar) supera
                lo disponible.
              </small>
            )}
          </CCol>
        </CRow>
      )}
      {/* Row 1: Batch selection — two ways */}
      <CFormGroup>
        <CRow className="align-items-center">
          {/* Manual select */}
          <CCol md={!disabled ? 2 : 12} className="mt-2">
            <CLabel>Lote:</CLabel>
          </CCol>
          <CCol md={4}>
            <InputBatchSelect
              value={batch}
              onChange={onBatchSelectChange}
              disabled={disabled}
              batchStatus="available"
            />
            <FieldErrors
              errors={
                getFieldErrors("inventoryInputBatchId", errors) as string[]
              }
            />
          </CCol>

          {/* Barcode scan — only in edit mode */}
          {!disabled && (
            <>
              <CCol md={2} className="mt-2">
                <CLabel>Reemplazar por código de barra:</CLabel>
              </CCol>
              <CCol md={4}>
                <CInput
                  type="text"
                  placeholder="Escanee y presione Enter..."
                  value={barcodeInput}
                  disabled={loadingBarcode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setBarcodeInput(e.target.value)
                  }
                  onKeyDown={onBarcodeKeyDown}
                />
              </CCol>
            </>
          )}
        </CRow>
      </CFormGroup>

      {/* Row 2: Batch info summary */}
      {batch && (
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={2} className="mt-1">
              <CLabel>Detalle del lote:</CLabel>
            </CCol>
            <CCol md={10}>
              <div
                className="border rounded p-2 bg-light d-flex align-items-center"
                style={{ gap: "16px" }}
              >
                <span>
                  <strong>{batch.inputMaterialIdentifier}</strong>
                </span>
                <span className="text-muted">
                  Prov: {batch.supplierName ?? "—"}
                </span>
                <span className="text-muted">
                  Cód: <code>{batch.fabricBarcode}</code>
                </span>
                {/* Only show availability in edit mode */}
                {!disabled && (
                  <>
                    <span>
                      Disponible:{" "}
                      <CBadge
                        color={availableInBatch! > 0 ? "success" : "danger"}
                      >
                        {availableInBatch?.toFixed(2)}
                      </CBadge>
                    </span>
                    <span>
                      Disponible efectivo:{" "}
                      <CBadge
                        color={effectiveAvailable! > 0 ? "info" : "danger"}
                      >
                        {effectiveAvailable?.toFixed(2)}
                      </CBadge>{" "}
                      <CTooltip
                        content={
                          "Disponible: cantidad física actual del lote. " +
                          "Disponible efectivo: lo que realmente puede usar " +
                          "en esta orden, es decir el disponible menos lo que " +
                          "ya comprometieron otras órdenes pendientes de " +
                          "descontar este lote."
                        }
                      >
                        <i
                          className="fa fa-info-circle text-info"
                          style={{ cursor: "help" }}
                        />
                      </CTooltip>
                    </span>
                  </>
                )}
              </div>
            </CCol>
          </CRow>
        </CFormGroup>
      )}

      {/* Row 3: Amount + fixed toggle */}
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2} className="mt-2">
            <CLabel>
              {editingItem.isFixedAmount
                ? "Cant. fija total:"
                : "Cant. por unidad:"}
            </CLabel>
          </CCol>
          <CCol md={2}>
            <CInput
              type="number"
              value={emptyValueOnUndefined(editingItem.amount)}
              onChange={onAmountChange}
              placeholder="Cant."
              step="0.01"
              disabled={disabled}
              max={availableInBatch || undefined}
              min={0}
            />
            <FieldErrors
              errors={getFieldErrors("amount", errors) as string[]}
            />
          </CCol>
          {disabled && (
            <CCol
              md={4}
              className="mt-2 d-flex align-items-center"
              style={{ gap: "8px" }}
            >
              {editingItem.isFixedAmount ? (
                <CBadge color="info">
                  <i className="fa fa-lock" /> Cantidad fija
                </CBadge>
              ) : (
                <CBadge color="secondary">
                  <i className="fa fa-times-circle" /> Cant. por unidad
                </CBadge>
              )}
            </CCol>
          )}
          {!disabled && (
            <>
              <CCol
                md={3}
                className="mt-2 d-flex align-items-center"
                style={{ gap: "8px" }}
              >
                <AnimatedCheckbox
                  value={editingItem.isFixedAmount ?? false}
                  onChange={onFixedAmountChange}
                />
                <CLabel className="mb-0">Cantidad fija</CLabel>
              </CCol>
              <CCol md={5} className="mt-2">
                {editingItem.isFixedAmount ? (
                  <small className="text-info">
                    <i className="fa fa-info-circle" /> Se descontarán
                    exactamente <strong>{editingItem.amount ?? "?"}</strong>{" "}
                    unidades de este lote, sin importar la cantidad producida.
                  </small>
                ) : (
                  <small className="text-muted">
                    <i className="fa fa-info-circle" /> Se descontará{" "}
                    <strong>
                      {editingItem.amount ?? "?"} × cant. producida
                    </strong>{" "}
                    de este lote.
                  </small>
                )}
              </CCol>
            </>
          )}
        </CRow>
      </CFormGroup>

      {!disabled && (
        <CFormGroup>
          <CRow>
            <CCol>
              <CButton
                className="btn btn-danger float-right"
                onClick={onDeleteClick}
              >
                <i className="fa fa-trash" />
              </CButton>
            </CCol>
          </CRow>
        </CFormGroup>
      )}
    </div>
  );
};

const propsAreEqual = (
  prev: ProductionInputMaterialFormProps,
  next: ProductionInputMaterialFormProps,
): boolean =>
  prev.value.id === next.value.id &&
  prev.value.inventoryInputBatch === next.value.inventoryInputBatch &&
  prev.value.amount === next.value.amount &&
  prev.value.isFixedAmount === next.value.isFixedAmount &&
  prev.value.inputMaterialId === next.value.inputMaterialId &&
  prev.value.inventoryInputBatchId === next.value.inventoryInputBatchId &&
  prev.committedFromOthers === next.committedFromOthers &&
  prev.isDuplicateInOrder === next.isDuplicateInOrder &&
  prev.isOvercommitted === next.isOvercommitted &&
  errorsAreSame(prev.errors, next.errors);

export default React.memo(ProductionInputMaterialForm, propsAreEqual);
